'use client'

import { cn } from '@/utils/styles/cn'
import { AnimatePresence, motion } from 'framer-motion'
import { Minus, Plus } from 'lucide-react'
import { useState } from 'react'

interface IFaqItem {
    question: string
    answer: React.ReactNode
}

interface IFaqAccordionItemProps {
    item: IFaqItem
    isExpanded: boolean
    onToggle: () => void
    className?: string
}

function FaqAccordionItem({ item, isExpanded, onToggle, className }: IFaqAccordionItemProps) {
    return (
        <div onClick={onToggle} className={cn('bg-white px-5 py-4 rounded-xl cursor-pointer select-none', className)}>
            <button className="w-full flex items-center justify-between text-left" aria-expanded={isExpanded}>
                <h3 className="!text-[15px] md:!text-[17px] !leading-snug !tracking-normal font-medium text-gray-900 pr-8">
                    {item.question}
                </h3>
                <div className="shrink-0">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                            key={isExpanded ? 'minus' : 'plus'}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.15 }}
                        >
                            {isExpanded ? (
                                <Minus className="size-5 text-gray-600" />
                            ) : (
                                <Plus className="size-5 text-gray-600" />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </button>

            {/* Answer is ALWAYS mounted so every answer ships in the SSR HTML (SEO-readable);
                only its height/opacity animate between collapsed and open. */}
            <motion.div
                initial={false}
                animate={{ opacity: isExpanded ? 1 : 0, height: isExpanded ? 'auto' : 0 }}
                transition={{
                    height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.2, ease: 'easeInOut' },
                }}
                onClick={(e) => e.stopPropagation()}
                className={cn('flex flex-col overflow-hidden', isExpanded && 'cursor-text')}
                aria-hidden={!isExpanded}
            >
                <div className="min-h-px w-full bg-[#E5E5E5] my-3" />
                <p className="text-gray-600 whitespace-pre-line leading-relaxed">{item.answer}</p>
            </motion.div>
        </div>
    )
}

interface IFaqAccordionProps {
    items: IFaqItem[]
    className?: string
    itemClassName?: string
    /** Allow multiple items to be expanded at once. Defaults to false. */
    allowMultiple?: boolean
}

export function FaqAccordion({ items, className, itemClassName, allowMultiple = false }: IFaqAccordionProps) {
    const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set())

    const toggleItem = (index: number) => {
        setExpandedIndices((prev) => {
            const next = new Set(allowMultiple ? prev : [])
            if (prev.has(index)) {
                next.delete(index)
            } else {
                next.add(index)
            }
            return next
        })
    }

    return (
        <div className={cn('flex flex-col gap-3', className)}>
            {items.map((item, index) => (
                <FaqAccordionItem
                    key={item.question}
                    item={item}
                    isExpanded={expandedIndices.has(index)}
                    onToggle={() => toggleItem(index)}
                    className={itemClassName}
                />
            ))}
        </div>
    )
}

export type { IFaqItem }
